'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { HelpCircle, Mail, Send, ChevronDown, ChevronUp, Sparkles, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { submitContactFormApi } from '@/lib/api/client';

export default function SupportPage() {
  const t = useTranslations('supportPage');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const targetEmail = 'raj.myinscribe@gmail.com';

  const faqs = [1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({
    q: t(`faq${n}Q`),
    a: t(`faq${n}A`),
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg(null);
    setErrorMsg(null);

    if (!email || !message) {
      setErrorMsg(t('validationError'));
      return;
    }

    try {
      setLoading(true);
      const data = await submitContactFormApi({ name, email, subject, message });
      setSuccessMsg(data.message || t('successFallback'));
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (err: any) {
      setErrorMsg(err.message || t('errorFallback'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto py-12 px-4 space-y-12">
      {/* Hero Section */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#5ab4ff]/10 border border-[#5ab4ff]/30 text-[#5ab4ff] text-xs font-semibold uppercase tracking-wider">
          <HelpCircle className="w-3.5 h-3.5 text-[#5ab4ff]" />
          <span>{t('eyebrow')}</span>
        </div>
        <h1 className="text-3xl sm:text-5xl font-['Lexend'] font-semibold text-white">{t('h1')}</h1>
        <p className="text-sm text-[rgba(220,236,255,0.75)] max-w-xl mx-auto leading-relaxed">
          {t('intro')}
        </p>
      </div>

      {/* Real Platform FAQ Section */}
      <div className="bg-[rgba(13,22,45,0.7)] backdrop-blur-xl border border-[rgba(255,255,255,0.12)] rounded-3xl p-6 sm:p-8 shadow-xl space-y-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-['Lexend'] font-semibold text-white mb-1.5">{t('faqHeading')}</h2>
          <p className="text-sm text-[rgba(220,236,255,0.7)] leading-relaxed">{t('faqSub')}</p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => {
            const isOpen = openFaqIndex === index;
            return (
              <div key={index} className="rounded-2xl bg-gradient-to-br from-white/10 to-white/[0.02] border border-white/15 shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] backdrop-blur-xl overflow-hidden transition-all duration-200">
                <button
                  type="button"
                  onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                  className="w-full px-5 py-4 text-left flex items-center justify-between text-sm font-medium text-white hover:text-[#5ab4ff] transition-colors gap-4 cursor-pointer"
                  aria-expanded={isOpen}
                >
                  <span>{faq.q}</span>
                  <span className="w-7 h-7 rounded-full bg-white/12 grid place-items-center text-sm font-bold text-white shrink-0">
                    {isOpen ? '-' : '+'}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 text-sm text-[rgba(220,236,255,0.85)] border-t border-white/10 pt-3 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Support Request Form */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        <div className="bg-[rgba(13,22,45,0.7)] backdrop-blur-xl border border-[rgba(255,255,255,0.12)] rounded-3xl p-6 shadow-xl space-y-4">
          <div>
            <h3 className="text-base font-['Lexend'] font-semibold text-white mb-1.5">{t('directEmailHeading')}</h3>
            <p className="text-sm text-[rgba(220,236,255,0.7)] leading-relaxed">{t('directEmailSub')}</p>
          </div>
          <div className="p-4 bg-[rgba(5,10,36,0.6)] border border-[rgba(255,255,255,0.08)] rounded-xl space-y-2">
            <div className="flex items-center gap-2 text-xs font-semibold text-[#5ab4ff] uppercase tracking-wider">
              <Mail className="w-4 h-4 text-[#5ab4ff] shrink-0" />
              <span>{t('inboxLabel')}</span>
            </div>
            <a href={`mailto:${targetEmail}`} className="text-sm font-medium text-[#5ab4ff] hover:text-[#8dcbff] hover:underline break-all block">
              {targetEmail}
            </a>
          </div>
        </div>

        <div className="lg:col-span-2 bg-[rgba(13,22,45,0.7)] backdrop-blur-xl border border-[rgba(255,255,255,0.12)] rounded-3xl p-6 sm:p-8 shadow-xl space-y-6">
          <div>
            <h2 className="text-xl font-['Lexend'] font-semibold text-white mb-1.5">{t('formHeading')}</h2>
            <p className="text-sm text-[rgba(220,236,255,0.7)] leading-relaxed">{t('formSub')}</p>
          </div>

          {successMsg && (
            <div className="p-4 rounded-xl bg-[rgba(141,255,201,0.12)] border border-[rgba(141,255,201,0.3)] flex items-start gap-3 text-[#8dffc9] text-xs font-medium">
              <CheckCircle2 className="w-4 h-4 text-[#8dffc9] shrink-0 mt-0.5" />
              <span>{successMsg}</span>
            </div>
          )}

          {errorMsg && (
            <div className="p-4 rounded-xl bg-[rgba(255,155,176,0.12)] border border-[rgba(255,155,176,0.3)] flex items-start gap-3 text-[#ff9bb0] text-xs font-medium">
              <AlertCircle className="w-4 h-4 text-[#ff9bb0] shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="support-name" className="block text-xs font-semibold text-[rgba(220,236,255,0.85)] uppercase tracking-wider mb-2">
                  {t('nameLabel')}
                </label>
                <input
                  id="support-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('namePlaceholder')}
                  className="w-full h-12 bg-[rgba(5,10,36,0.6)] border border-[rgba(255,255,255,0.14)] rounded-xl px-4 text-sm text-white placeholder-[rgba(220,236,255,0.4)] focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff] transition-all"
                />
              </div>

              <div>
                <label htmlFor="support-email" className="block text-xs font-semibold text-[rgba(220,236,255,0.85)] uppercase tracking-wider mb-2">
                  {t('emailFieldLabel')} <span className="text-[#5ab4ff]">*</span>
                </label>
                <input
                  id="support-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('emailPlaceholder')}
                  className="w-full h-12 bg-[rgba(5,10,36,0.6)] border border-[rgba(255,255,255,0.14)] rounded-xl px-4 text-sm text-white placeholder-[rgba(220,236,255,0.4)] focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff] transition-all"
                />
              </div>
            </div>

            <div>
              <label htmlFor="support-subject" className="block text-xs font-semibold text-[rgba(220,236,255,0.85)] uppercase tracking-wider mb-2">
                {t('subjectLabel')}
              </label>
              <input
                id="support-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('subjectPlaceholder')}
                className="w-full h-12 bg-[rgba(5,10,36,0.6)] border border-[rgba(255,255,255,0.14)] rounded-xl px-4 text-sm text-white placeholder-[rgba(220,236,255,0.4)] focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff] transition-all"
              />
            </div>

            <div>
              <label htmlFor="support-message" className="block text-xs font-semibold text-[rgba(220,236,255,0.85)] uppercase tracking-wider mb-2">
                {t('messageLabel')} <span className="text-[#5ab4ff]">*</span>
              </label>
              <textarea
                id="support-message"
                required
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('messagePlaceholder')}
                className="w-full bg-[rgba(5,10,36,0.6)] border border-[rgba(255,255,255,0.14)] rounded-xl px-4 py-3 text-sm text-white placeholder-[rgba(220,236,255,0.4)] focus:outline-none focus:border-[#5ab4ff] focus:ring-1 focus:ring-[#5ab4ff] transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-white hover:bg-[#e6edff] text-[#050a24] font-semibold text-sm shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff] active:scale-[0.98]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('submittingButton')}</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>{t('submitButton')}</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
